import { Badge } from "@/components/ui/badge";
import { Building2, Calendar, Check, Copy, Phone, PhoneCall, MessageCircle, FileText, Globe, Hash, Download, Database, AlertCircle, Clock, Users, User, Bot, Timer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatDuration, intervalToDuration } from "date-fns";
import { Call } from "@/api/calls/calls.types";
import { downloadFile } from "@/utils/download";
import { formatPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import { FadeId } from "@/components/ui/fade-id";

interface OverviewTabProps {
    call: Call;
    copiedId: string | null;
    copyToClipboard: (id: string) => void;
}

function getCallDuration(startTime?: string | null, endTime?: string | null): string {
    if (!startTime || !endTime) return 'In Progress';

    const duration = intervalToDuration({
        start: new Date(startTime),
        end: new Date(endTime)
    });

    return formatDuration(duration, { format: ['minutes', 'seconds'] });
}

function formatTimestamp(dateString?: string | null): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

function getStatusColor(status?: string, endedReason?: string | null): string {
    if (!status) return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100';

    switch (status.toLowerCase()) {
        case 'completed':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
        case 'in_progress':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100';
        case 'failed':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
        case 'ended':
            if (endedReason?.includes('completed')) {
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
            }
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100';
    }
}

export function OverviewTab({ call, copiedId, copyToClipboard }: OverviewTabProps) {
    const messageCount = call.messages?.length || 0;
    const userMessages = call.messages?.filter(m => m.role === 'user').length || 0;
    const botMessages = call.messages?.filter(m => m.role === 'bot').length || 0;

    return (
        <div className="space-y-6">
            {/* First Row - Main Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Call Overview */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <PhoneCall className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Call Overview</CardTitle>
                                <CardDescription className="text-xs text-muted-foreground/70">Status and type</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                                Status
                            </span>
                            <Badge 
                                variant="secondary"
                                className={getStatusColor(call.status, call.endedReason)}
                            >
                                {call.status?.toUpperCase() || 'UNKNOWN'}
                            </Badge>
                        </div>
                        {call.endedReason && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                                    End Reason
                                </span>
                                <Badge variant="outline">
                                    {call.endedReason.split('-').map(word => 
                                        word.charAt(0).toUpperCase() + word.slice(1)
                                    ).join(' ')}
                                </Badge>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <PhoneCall className="h-4 w-4 text-muted-foreground" />
                                Type
                            </span>
                            <Badge variant="outline" className="flex items-center gap-1">
                                {call.type === 'webCall' && <Globe className="h-3 w-3" />}
                                {call.type === 'inboundPhoneCall' ? 'Inbound Phone Call' :
                                 call.type === 'outboundPhoneCall' ? 'Outbound Phone Call' :
                                 call.type === 'webCall' ? 'Web Call' :
                                 'Unknown'}
                            </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Timer className="h-4 w-4 text-muted-foreground" />
                                Duration
                            </span>
                            <span className="text-sm font-medium">
                                {getCallDuration(call.startedAt, call.endedAt)}
                            </span>
                        </div>
                    </CardContent>
                </Card>

                {/* Conversation Stats */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <MessageCircle className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Conversation</CardTitle>
                                <CardDescription className="text-xs text-muted-foreground/70">Message statistics</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <MessageCircle className="h-4 w-4 text-muted-foreground" />
                                Total Messages
                            </span>
                            <Badge variant="outline">{messageCount}</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                User Messages
                            </span>
                            <Badge variant="outline">{userMessages}</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Bot className="h-4 w-4 text-muted-foreground" />
                                Bot Messages
                            </span>
                            <Badge variant="outline">{botMessages}</Badge>
                        </div>
                    </CardContent>
                </Card>

                {/* Connection Details */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                {call.type === 'webCall' ? <Globe className="h-4 w-4 text-primary" /> : <Building2 className="h-4 w-4 text-primary" />}
                            </div>
                            <div>
                                <CardTitle className="text-base">Connection</CardTitle>
                                <CardDescription className="text-xs text-muted-foreground/70">{call.type === 'webCall' ? 'Web channel' : 'Phone channel'}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                        {call.type === 'webCall' ? (
                            <>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium flex items-center gap-2">
                                        <Globe className="h-4 w-4 text-muted-foreground" />
                                        Transport
                                    </span>
                                    <Badge variant="outline">WEB BROWSER</Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium flex items-center gap-2">
                                        <User className="h-4 w-4 text-muted-foreground" />
                                        Customer
                                    </span>
                                    <Badge variant="outline">Web User</Badge>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium flex items-center gap-2">
                                        <Building2 className="h-4 w-4 text-muted-foreground" />
                                        Provider
                                    </span>
                                    <Badge variant="outline">
                                        {call.phoneCallProvider?.toUpperCase() || 'N/A'}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-muted-foreground" />
                                        Transport
                                    </span>
                                    <Badge variant="outline">
                                        {call.phoneCallTransport?.toUpperCase() || 'N/A'}
                                    </Badge>
                                </div>
                                {(call.customer?.number || call.customer?.name) && (
                                    <>
                                        {call.customer.name && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium flex items-center gap-2">
                                                    <User className="h-4 w-4 text-muted-foreground" />
                                                    Customer Name
                                                </span>
                                                <span className="text-sm">{call.customer.name}</span>
                                            </div>
                                        )}
                                        {call.customer.number && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium flex items-center gap-2">
                                                    <Phone className="h-4 w-4 text-muted-foreground" />
                                                    Customer Number
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm">
                                                        {formatPhoneNumber(call.customer.number) || call.customer.number}
                                                    </span>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 shrink-0"
                                                        onClick={() => copyToClipboard(call.customer?.number!)}
                                                    >
                                                        {copiedId === call.customer.number ? (
                                                            <Check className="h-3.5 w-3.5 text-green-500" />
                                                        ) : (
                                                            <Copy className="h-3.5 w-3.5" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        {call.customer.extension && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium flex items-center gap-2">
                                                    <Hash className="h-4 w-4 text-muted-foreground" />
                                                    Extension
                                                </span>
                                                <span className="text-sm font-mono">{call.customer.extension}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Second Row - System Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* System IDs */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Database className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">System IDs</CardTitle>
                                <CardDescription className="text-xs text-muted-foreground/70">Resource identifiers</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                        <div className="flex items-center justify-between gap-2 min-w-0">
                            <span className="text-sm font-medium flex items-center gap-2 shrink-0">
                                <Hash className="h-4 w-4 text-muted-foreground" />
                                Call ID
                            </span>
                            <div className="min-w-0 flex-1 flex justify-end">
                                <FadeId 
                                    id={call.id} 
                                    label="Call ID"
                                    tooltipSide="left"
                                    height="h-8"
                                    className="max-w-[180px]"
                                    idWidth="100%"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 min-w-0">
                            <span className="text-sm font-medium flex items-center gap-2 shrink-0">
                                <Hash className="h-4 w-4 text-muted-foreground" />
                                Assistant ID
                            </span>
                            <div className="min-w-0 flex-1 flex justify-end">
                                <FadeId 
                                    id={call.assistantId} 
                                    label="Assistant ID"
                                    tooltipSide="left"
                                    height="h-8"
                                    className="max-w-[180px]"
                                    idWidth="100%"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 min-w-0">
                            <span className="text-sm font-medium flex items-center gap-2 shrink-0">
                                <Hash className="h-4 w-4 text-muted-foreground" />
                                Organization ID
                            </span>
                            <div className="min-w-0 flex-1 flex justify-end">
                                <FadeId 
                                    id={call.organizationId} 
                                    label="Organization ID"
                                    tooltipSide="left"
                                    height="h-8"
                                    className="max-w-[180px]"
                                    idWidth="100%"
                                />
                            </div>
                        </div>
                        {call.phoneNumberId && (
                            <div className="flex items-center justify-between gap-2 min-w-0">
                                <span className="text-sm font-medium flex items-center gap-2 shrink-0">
                                    <Hash className="h-4 w-4 text-muted-foreground" />
                                    Phone Number ID
                                </span>
                                <div className="min-w-0 flex-1 flex justify-end">
                                    <FadeId 
                                        id={call.phoneNumberId} 
                                        label="Phone Number ID"
                                        tooltipSide="left"
                                        height="h-8"
                                        className="max-w-[180px]"
                                        idWidth="100%"
                                    />
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Timestamps */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Calendar className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Timestamps</CardTitle>
                                <CardDescription className="text-xs text-muted-foreground/70">Call timeline</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                Created At
                            </span>
                            <span className="text-sm">
                                {formatTimestamp(call.createdAt)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                Started At
                            </span>
                            <span className="text-sm">
                                {formatTimestamp(call.startedAt)}
                            </span>
                        </div>
                        {call.endedAt && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    Ended At
                                </span>
                                <span className="text-sm">
                                    {formatTimestamp(call.endedAt)}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                Last Updated
                            </span>
                            <span className="text-sm">
                                {formatTimestamp(call.updatedAt)}
                            </span>
                        </div>
                    </CardContent>
                </Card>

                {/* Recordings */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <FileText className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">Recordings</CardTitle>
                                <CardDescription className="text-xs text-muted-foreground/70">Audio files</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                        {call.recordingUrl ? (
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    Mono Recording
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadFile(call.recordingUrl!, `${call.id}-mono.mp3`)}
                                    className="gap-1.5 h-8 px-3"
                                >
                                    <Download className="h-3.5 w-3.5" />
                                    <span className="text-xs">Download</span>
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    Mono Recording
                                </span>
                                <Badge variant="outline" className="text-xs">Not Available</Badge>
                            </div>
                        )}
                        {call.stereoRecordingUrl ? (
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    Stereo Recording
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadFile(call.stereoRecordingUrl!, `${call.id}-stereo.mp3`)}
                                    className="gap-1.5 h-8 px-3"
                                >
                                    <Download className="h-3.5 w-3.5" />
                                    <span className="text-xs">Download</span>
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    Stereo Recording
                                </span>
                                <Badge variant="outline" className="text-xs">Not Available</Badge>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
